import { memo, useCallback, useEffect, useState } from "react";
import type { FallbackTier } from "@chula/shared";
import type { CctvCamera } from "../map/layers";
import { PanelHeader } from "./PanelHeader";
import { PanelState, listFeedState } from "./PanelState";
import { CvTileOverlay, CvVisionHeader, CvVisionNote } from "./CvVisionStrip";
import { useCvWall } from "./useCvWall";

interface Props {
  cameras: CctvCamera[];
  loading?: boolean;
  samplingEnabled?: boolean;
  ageMinutes?: number | null;
  fallbackTier?: FallbackTier;
  onSelect: (camera: CctvCamera) => void;
  /** Refetch the feed — shown as Retry when the source is offline. */
  onRetry?: () => void;
  onOpenCameras?: () => void;
  /** API base for the CORS-clean frame relay the CV reads through. */
  apiBase?: string;
}

const MAX_TILES = 6;
/** How often a dead tile is re-probed in the background. A transient relay
 *  timeout must not leave a camera marked NO SIGNAL until the page reloads. */
const RECHECK_MS = 60_000;

/** Every Longdo camera name starts with its province in brackets — "(กรุงเทพมหานคร) จุฬา 12 …".
 *  On a campus wall that prefix is the same on every tile, and at tile width it
 *  is all the caption has room for, so it hid the one part that differs. */
export function cameraLabel(name: string): string {
  return name.replace(/^\([^)]*\)\s*/, "") || name;
}

/** The relay is the only path a frame can take — see /api/cctv/snapshot/:id. */
function frameUrl(apiBase: string, id: string): string {
  return `${apiBase}/api/cctv/snapshot/${encodeURIComponent(id)}`;
}

/**
 * LIVE CCTV — thumbnail strip of Longdo traffic cameras around campus.
 * Click a tile to open the same CctvStreamModal a map-pin click opens.
 *
 * Lives in the IOC sidebar, delivering on the lens's own description
 * ("...air, flood gauges, CCTV, and citizen reports on one strategic
 * view") — previously CCTV was only reachable via a small map pin, with
 * no at-a-glance surface in the executive brief.
 *
 * Each tile also carries a live computer-vision readout (haze, motion) computed
 * in the browser from the same frame the operator is looking at. See
 * components/CvVisionStrip.tsx and cv/ for what is measured and what is not.
 */
export const LiveCctvPanel = memo(function LiveCctvPanel({
  cameras,
  loading,
  samplingEnabled = true,
  ageMinutes,
  fallbackTier,
  onSelect,
  onRetry,
  onOpenCameras,
  apiBase = "",
}: Props) {
  const tiles = cameras.slice(0, MAX_TILES);
  const cv = useCvWall(cameras, apiBase, samplingEnabled && !loading && tiles.length > 0);
  // Frames that failed to arrive. A dead vendor host leaves an <img> loading
  // forever behind an empty box; the relay answers 504 instead, and that is
  // what lets the panel say so rather than show six blank tiles.
  const [failed, setFailed] = useState<ReadonlySet<string>>(() => new Set());
  const [attempt, setAttempt] = useState(0);
  const markFailed = useCallback((id: string) => {
    setFailed((prev) => (prev.has(id) ? prev : new Set([...prev, id])));
  }, []);
  const retryFrames = useCallback(() => {
    setFailed(new Set());
    setAttempt((n) => n + 1);
    onRetry?.();
  }, [onRetry]);
  // Re-probe dead cameras off-screen; a tile comes back only once a frame
  // actually loads, so a still-dead host does not flicker the grid.
  useEffect(() => {
    if (failed.size === 0) return;
    const timer = setInterval(() => {
      for (const id of failed) {
        const probe = new Image();
        probe.onload = () => setFailed((prev) => {
          if (!prev.has(id)) return prev;
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
        probe.src = `${frameUrl(apiBase, id)}?recheck=${Date.now()}`;
      }
    }, RECHECK_MS);
    return () => clearInterval(timer);
  }, [failed, apiBase]);
  const withFrames = tiles.filter((t) => t.imageUrl);
  const allDead = withFrames.length > 0 && withFrames.every((t) => failed.has(t.id));

  const state = listFeedState({ loading, count: tiles.length, tier: fallbackTier, ageMinutes });
  const header = (
    <PanelHeader
      title="TRAFFIC CAMERAS"
      ageMinutes={ageMinutes}
      fallbackTier={fallbackTier ?? null}
      source="longdo"
      actions={<>{tiles.length > 0 && <span className="caption mono">{cameras.length}</span>}{onOpenCameras && <button type="button" onClick={onOpenCameras} aria-label="Open camera operations">DESK ↗</button>}</>}
    />
  );

  if (state === "loading" || state === "empty" || state === "error") {
    return (
      <div className="left-section cctv-panel">
        {header}
        <PanelState kind={state} what="traffic cameras" source="Longdo" ageMinutes={ageMinutes} onRetry={onRetry} />
      </div>
    );
  }

  return (
    <div className="left-section cctv-panel">
      {header}
      {state === "stale" && <PanelState kind="stale" what="camera list" ageMinutes={ageMinutes} />}
      <CvVisionHeader view={cv} />
      {allDead ? (
        <p className="cctv-offline caption">
          <span>No camera is sending frames — the host is not answering.</span>
          <button type="button" onClick={retryFrames}>Retry</button>
        </p>
      ) : (
        <div className="cctv-tile-grid">
          {tiles.map((cam) => {
            const label = cameraLabel(cam.name);
            const dead = failed.has(cam.id);
            return (
              <button
                key={cam.id}
                type="button"
                className="cctv-tile"
                onClick={() => onSelect(cam)}
                title={`Open live view — ${cam.name}`}
              >
                {cam.imageUrl && !dead ? (
                  <img
                    key={attempt}
                    src={frameUrl(apiBase, cam.id)}
                    alt=""
                    loading="lazy"
                    onError={() => markFailed(cam.id)}
                  />
                ) : (
                  <span className="cctv-tile-empty">{dead ? "NO SIGNAL" : "NO FRAME"}</span>
                )}
                {!dead && <CvTileOverlay r={cv.byCamera[cam.id]} />}
                <span className="cctv-tile-name mono">{label}</span>
              </button>
            );
          })}
        </div>
      )}
      {cv.analysed > 0 && <CvVisionNote />}
    </div>
  );
});
